import "./MobileNavbar.scss";

import React, {
  cloneElement,
  isValidElement,
  memo,
  ReactElement,
  useState,
} from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { NavLink, useParams } from "react-router-dom";

import { Logo } from "@/assets/svg/svgIcons";
import useHeaderBannerStore from "@/covaltech-react-ui/stores/HeaderBannerStore";

import ElementNavBar from "../ElementNavbar/ElementNavbar";
import { NavItemType } from "../Navbar";
import SubMenuNavbar from "../SubMenuNavbar/SubMenuNavbar";

interface MobileNavbarProps {
  navItems: NavItemType[];
  mobileBeforeChildren?: React.ReactNode;
  children?: React.ReactNode;
  showLinkLogo?: boolean;
  scrollToContainer?: boolean;
}
const MobileNavbar: React.FC<MobileNavbarProps> = ({
  navItems,
  mobileBeforeChildren,
  children,
  showLinkLogo,
  scrollToContainer = false,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const { visible, height } = useHeaderBannerStore();

  const toggleNavbar = () => {
    setCollapsed(!collapsed);
    // scroll to the .container if the navbar is collapsed and scrollToContainer is true
    if (collapsed && scrollToContainer) {
      const container = document.querySelector(".container");
      if (container) {
        container.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const { lang } = useParams<{ lang: string }>();
  const homeLink = lang ? `/${lang}/` : "/";

  const handleLogoClick = () => {
    // When already on the home route, NavLink does not navigate, so scroll
    // back to the top manually.
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <nav
      className={`covMobileNavbar`}
      role="navigation"
      aria-label="Site Navigation"
      style={{ marginTop: visible ? height : 0 }}
    >
      <div className="navbar-toggler">
        <NavLink
          to={homeLink}
          className="navLogoLink"
          aria-label="CovalTech"
          onClick={handleLogoClick}
        >
          <Logo className="navLogo" />
          <span className="logoText">
            Coval<span className="accentText">Tech</span>
          </span>
        </NavLink>
        <button
          type="button"
          className="burger-btn"
          onClick={toggleNavbar}
          aria-label={
            collapsed ? "Close navigation menu" : "Open navigation menu"
          }
          aria-expanded={collapsed}
          aria-controls="mobile-navbar-collapse"
        >
          {collapsed ? <FaTimes /> : <FaBars />}
        </button>
      </div>
      <div
        id="mobile-navbar-collapse"
        className={`navbar-collapse ${collapsed ? "show" : ""}`}
      >
        <ul className="navbar-nav">
          {isValidElement(mobileBeforeChildren) && (
            <li className="nav-item">
              {cloneElement(
                mobileBeforeChildren as ReactElement<{
                  toggleNavbar?: () => void;
                }>,
                { toggleNavbar },
              )}
            </li>
          )}

          {navItems.map((item) =>
            item.type === "submenu" ? (
              <SubMenuNavbar
                key={item.text}
                navItem={item}
                showLinkLogo={showLinkLogo}
                toggleNavbar={toggleNavbar}
              />
            ) : (
              <ElementNavBar
                key={item.href + item.text}
                navItem={item}
                showLinkLogo={showLinkLogo}
                onLinkClick={toggleNavbar}
              />
            ),
          )}
        </ul>
        {/* pass toggleNavbar to children */}
        {children &&
          cloneElement(
            children as ReactElement<{ toggleNavbar?: () => void }>,
            { toggleNavbar },
          )}
      </div>
    </nav>
  );
};

export default memo(MobileNavbar);
